import { debounce } from '@mui/material';
import { portalPaths, UrlQueryParams } from '@provision/portal-routes';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useCopyToClipboard } from 'src/utils/hooks';
import { redactSelection } from './redaction.utils';

export function useMessageContextMenu({
  id,
  message,
  conversationUrl,
  onRedactStart,
  onConfirm,
  container,
}: {
  id: string;
  message: string;
  conversationUrl: string;
  onRedactStart: () => void;
  onConfirm: (message: string) => void;
  container: HTMLDivElement | null;
}) {
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectionCoordinates, setSelectionCoordinates] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const [selectedText, setSelectedText] = useState('');
  const [redactedText, setRedactedText] = useState(message);
  const [startPos, setStartPos] = useState<{ clientX: number; clientY: number } | null>(null);

  useEffect(() => {
    setRedactedText(message);
  }, [message]);

  const open = Boolean(menuAnchorEl);
  const [isRedacting, setIsRedacting] = useState(false);

  const handleMenuClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    event.preventDefault();
    setMenuAnchorEl(event.currentTarget);
  }, []);

  const handleMenuClose = useCallback(() => {
    setMenuAnchorEl(null);
  }, []);

  const handleRedactClick = useCallback(() => {
    setIsRedacting(true);
    onRedactStart();
    handleMenuClose();
  }, [handleMenuClose, onRedactStart]);

  const handleOpenOriginalEmailClick = useCallback(() => {
    openInNewHeadlessWindow(portalPaths.originalEmail(id));
    handleMenuClose();
  }, [handleMenuClose, id]);

  const { copyToClipboard } = useCopyToClipboard();

  const handleCopyMessageIdToClipboard = useCallback(async () => {
    await copyToClipboard(id);
    handleMenuClose();
  }, [copyToClipboard, handleMenuClose, id]);

  const { copyToClipboard: copyMessageLink } = useCopyToClipboard({
    successMessage: 'Message link copied.',
  });

  const handleCopyMessageLinkToClipboard = useCallback(async () => {
    if (!conversationUrl) return;
    const url = new URL(conversationUrl);
    url.searchParams.set(UrlQueryParams.messageId, id);

    await copyMessageLink(url.toString());
    handleMenuClose();
  }, [conversationUrl, copyMessageLink, handleMenuClose, id]);

  const handlePointerDown = useCallback((event: PointerEvent) => {
    setStartPos({ clientX: event.clientX, clientY: event.clientY });
  }, []);

  const handleTextSelection = useMemo(
    () =>
      debounce((event: PointerEvent) => {
        if (!isRedacting) return;

        const selection = window.getSelection();
        const selectedText = selection?.toString() || '';
        if (selection && selectedText) {
          const x = startPos ? (startPos.clientX + event.clientX) / 2 : event.clientX;
          const y = startPos ? (startPos.clientY + event.clientY) / 2 : event.clientY;
          setSelectionCoordinates({ top: y, left: x });
          setSelectedText(selectedText);
        } else {
          setSelectionCoordinates(null);
          setSelectedText('');
        }
        setStartPos(null);
      }, 200),
    [isRedacting, startPos],
  );

  const handleRedactSelection = useCallback(() => {
    if (!selectedText) return;

    setRedactedText(redactSelection(redactedText, selectedText));
    setSelectionCoordinates(null);
    setSelectedText('');
  }, [selectedText, redactedText]);

  const handleCancelRedaction = useCallback(() => {
    setIsRedacting(false);
    setRedactedText(message);
    setSelectionCoordinates(null);
    setSelectedText('');
  }, [message]);

  const handleConfirmRedaction = useCallback(() => {
    onConfirm?.(redactedText);
    setIsRedacting(false);
    setSelectionCoordinates(null);
    setSelectedText('');
  }, [onConfirm, redactedText]);

  useEffect(() => {
    if (!container) {
      return;
    }

    if (isRedacting) {
      container.addEventListener('pointerdown', handlePointerDown);
      container.addEventListener('pointerup', handleTextSelection);
    }
    return () => {
      container.removeEventListener('pointerdown', handlePointerDown);
      container.removeEventListener('pointerup', handleTextSelection);
    };
  }, [isRedacting, handleTextSelection, handlePointerDown, container]);

  return {
    menuAnchorEl,
    open,
    handleMenuClick,
    handleMenuClose,
    handleRedactClick,
    handleOpenOriginalEmailClick,
    handleCopyMessageIdToClipboard,
    handleCopyMessageLinkToClipboard,
    handleRedactSelection,
    handleCancelRedaction,
    handleConfirmRedaction,
    selectionCoordinates,
    setSelectionCoordinates,
    redactedText,
    isRedacting,
  };
}

function openInNewHeadlessWindow(route: string): void {
  const root = `${window.location.protocol}//${window.location.host}`;
  window.open(
    root + route,
    '_blank',
    'toolbar=no,location=no,status=no,menubar=no,scrollbars=yes,resizable=yes,width=600,height=650',
  );
}
